import { RootState } from '../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../helpers/common';
import { InviteStatus } from '../interfaces/IInviteStatus';
import { IInvitation } from '../interfaces/IInvitation';

export interface IAdmin {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  role: 'client' | 'admin' | 'owner';
  avatar: string | null;
  status: InviteStatus;
  invitation?: IInvitation | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface IAdminsListResponse {
  list: IAdmin[];
  total: number;
  totalPages: number;
}

export interface IAdminsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
}

export const adminsApi = createApi({
  reducerPath: 'adminsApi',
  tagTypes: ['Admin'],
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    getAdmins: build.query<IAdminsListResponse, IAdminsQueryParams | void>({
      transformResponse: (response: { data: IAdminsListResponse }) =>
        response.data,
      query: (params: any = {}) => {
        const queryParams = new URLSearchParams();
        
        params.page && queryParams.append('page', String(params.page));
        params.limit && queryParams.append('limit', String(params.limit));
        params.search && queryParams.append('search', params.search);
        
        return {
          url: `/admins/list${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Admin'],
    }),
    deactivateAdmin: build.mutation<void, string>({
      query: (adminId) => ({
        url: `/admins/${adminId}/deactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Admin'],
    }),
    reactivateAdmin: build.mutation<void, string>({
      query: (adminId) => ({
        url: `/admins/${adminId}/reactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Admin'],
    }),
  }),
});

export const {
  useGetAdminsQuery,
  useLazyGetAdminsQuery,
  useDeactivateAdminMutation,
  useReactivateAdminMutation,
} = adminsApi;

